import React, { useCallback, useContext, useState } from "react";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";
import { withRouter } from "react-router-dom";
import { RouteComponentProps } from "react-router";
import { Trans, useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { UI } from "@wwimmo/ui";
import styles from "src/screens/communicationSettings/CommunicationSettings.module.css";

const COPIED_FEEDBACK_MS = 1500;

interface CopyableEmailProps {
    email: string;
}

const CopyableEmail = ({ email }: CopyableEmailProps) => {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);

    const onCopy = useCallback(() => {
        navigator.clipboard.writeText(email);
        setCopied(true);
        window.setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
    }, [email]);

    const onKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onCopy();
        }
    };

    return (
        <span
            className={styles.CopyableEmail}
            onClick={onCopy}
            onKeyDown={onKeyDown}
            role="button"
            tabIndex={0}
            title={email}
        >
            {copied ? t("screens.communication_settings.krediflow.invoice_inbox.copied") : email}
            <span className={styles.CopyableEmailIcon} aria-hidden="true">
                {copied ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                    </svg>
                ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </svg>
                )}
            </span>
        </span>
    );
};

const InvoiceInboxSettingsBase = (props: RouteComponentProps) => {
    const { t } = useTranslation();
    const { communicationSettingsStore, navStore } = useContext(RootStoreContext);

    const inboxEmail = `rechnung.${navStore.customer?.subdomain ?? ""}@wwportal.ch`;

    const onChangeNotifyEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
        communicationSettingsStore.updateKrediFlowNotifyEmail(event.target.value);
    };

    return (
        <UI.Row>
            <UI.Col md={12}>
                <UI.Card
                    title={t("screens.communication_settings.krediflow.invoice_inbox.title").toString()}
                    className={styles.SettingsContainer}
                >
                    <UI.Row className="align-items-center mb-3">
                        <UI.Col>
                            <span>
                                <Trans
                                    i18nKey="screens.communication_settings.krediflow.invoice_inbox.explanation_text_1"
                                    components={{ emailchip: <CopyableEmail email={inboxEmail} /> }}
                                />
                            </span>
                            <span className="d-block mt-4">
                                {t("screens.communication_settings.krediflow.invoice_inbox.explanation_text_2")}
                            </span>
                        </UI.Col>
                    </UI.Row>
                    <UI.Row className="align-items-center mb-3">
                        <UI.Col md={4} lg={3}>
                            <label className="m-0 bold-text" style={{ color: "var(--primary)" }}>
                                {t("screens.communication_settings.krediflow.invoice_inbox.email_address")}
                            </label>
                        </UI.Col>
                        <UI.Col md={8} lg={6} className={styles.InputCol}>
                            <UI.Input
                                type="text"
                                value={communicationSettingsStore.krediFlowNotifyEmail}
                                onChange={onChangeNotifyEmail}
                                errorMsg={communicationSettingsStore.krediFlowNotifyEmailError}
                            />
                        </UI.Col>
                    </UI.Row>
                </UI.Card>
            </UI.Col>
        </UI.Row>
    );
};

export const InvoiceInboxSettings = withRouter(Sentry.withProfiler(observer(InvoiceInboxSettingsBase)));
